import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {useExecuteGenerate} from '../../../hooks/productions/useProductions';
import {ExecuteGenerateRequestBody} from '../../../api/types/production.type';
import '../Demo.scss';

const ExecuteGenerateApiTest: React.FC = () => {
  const [productionId, setProductionId] = useState<number>(59);
  const [moduleType, setModuleType] = useState<string>('PRODUCTION');
  const [isMarkComplete, setIsMarkComplete] = useState<boolean>(false);
  const [shouldContinue, setShouldContinue] = useState<boolean>(false);
  const [canGenerate, setCanGenerate] = useState<boolean>(false);
  const [canRegenerate, setCanRegenerate] = useState<boolean>(true);
  const [securityGroupIdForProduction, setSecurityGroupIdForProduction] =
    useState<number>(1);
  const [productionType, setProductionType] = useState<number>(1);
  const [productionStateCode, setProductionStateCode] =
    useState<string>('AFTERDATAGENFAILED');
  const [productionTempararyFolderPath, setProductionTempararyFolderPath] =
    useState<string>(
      '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\destinationlocation\\dndproject\\CADE5C9F-B105-4A5A-ACA2-AFBD3096E55E\\Temp\\Production59'
    );
  const [productionDestinationFolderPath, setProductionDestinationFolderPath] =
    useState<string>(
      '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\Productions\\dndproject3\\pprod'
    );
  const [productionFolderPath, setProductionFolderPath] = useState<string>(
    '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\destinationlocation\\dndproject\\CADE5C9F-B105-4A5A-ACA2-AFBD3096E55E\\57377E09-5AA8-48EC-97D2-3652B8D7B34D'
  );
  const [shouldPerformBlankPageDetection, setShouldPerformBlankPageDetection] =
    useState<boolean>(false);
  const [regenerateType, setRegenerateType] = useState<number>(0);

  const [executeResult, setExecuteResult] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const executeMutation = useExecuteGenerate(
    (data) => {
      setExecuteResult(`✅ ${data.message}`);
    },
    (error) => {
      setExecuteResult(`❌ Execute failed: ${error.message}`);
    }
  );

  const handleExecute = () => {
    const requestBody: ExecuteGenerateRequestBody = {
      ProductionId: productionId,
      ModuleType: moduleType,
      IsMarkComplete: isMarkComplete,
      ShouldContinue: shouldContinue,
      CanGenerate: canGenerate,
      CanRegenerate: canRegenerate,
      SecurityGroupIdForProduction: securityGroupIdForProduction,
      ProductionType: productionType,
      ProductionStateCode: productionStateCode,
      ProductionTempararyFolderPath: productionTempararyFolderPath,
      ProductionDestinationFolderPath: productionDestinationFolderPath,
      ProductionFolderPath: productionFolderPath,
      ShouldPerformBlankPageDetection: shouldPerformBlankPageDetection,
      RegenerateType: regenerateType,
    };
    executeMutation.mutate(requestBody);
  };

  const handleReset = () => {
    setExecuteResult('');
  };

  const handleCopyJson = async () => {
    if (executeMutation.data) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(executeMutation.data, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Execute Generate API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Execute production generation. Starts the generation process with the
          specified production details and returns a success message.
        </Typography>
      </Box>

      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="POST" color="success" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Execute Generate
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/generate/execute
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Execute production generation. POST request with request body
            containing production details, folder paths, and generation
            settings. Returns a message indicating that generation has started
            successfully.
          </Typography>
        </CardContent>
      </Card>

      {/* Request Body */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Request Body
          </Typography>

          {/* Basic Information */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production ID"
                  type="number"
                  value={productionId}
                  onChange={(e) => setProductionId(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Module Type"
                  value={moduleType}
                  onChange={(e) => setModuleType(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Security Group ID For Production"
                  type="number"
                  value={securityGroupIdForProduction}
                  onChange={(e) =>
                    setSecurityGroupIdForProduction(Number(e.target.value))
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production Type"
                  type="number"
                  value={productionType}
                  onChange={(e) => setProductionType(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production State Code"
                  value={productionStateCode}
                  onChange={(e) => setProductionStateCode(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Regenerate Type"
                  type="number"
                  value={regenerateType}
                  onChange={(e) => setRegenerateType(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          {/* Flags */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Flags
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="is-mark-completed"
                  control={
                    <Checkbox
                      data-testid="execute-generate-api-test-is-mark-complete"
                      checked={isMarkComplete}
                      onChange={(e) => setIsMarkComplete(e.target.checked)}
                    />
                  }
                  label="Is Mark Complete"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="is-mark-should-complete"
                  control={
                    <Checkbox
                      data-testid="execute-generate-api-test-should-continue"
                      checked={shouldContinue}
                      onChange={(e) => setShouldContinue(e.target.checked)}
                    />
                  }
                  label="Should Continue"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                 data-testid="can-generate-form-label"
                  control={
                    <Checkbox
                      data-testid="execute-generate-api-test-can-generate"
                      checked={canGenerate}
                      onChange={(e) => setCanGenerate(e.target.checked)}
                    />
                  }
                  label="Can Generate"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="can-re-generate-form-label"
                  control={
                    <Checkbox
                      data-testid="execute-generate-api-test-can-regenerate"
                      checked={canRegenerate}
                      onChange={(e) => setCanRegenerate(e.target.checked)}
                    />
                  }
                  label="Can Regenerate"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                 data-testid="should-perform-blank-page-detection-form-label"
                  control={
                    <Checkbox
                      data-testid="execute-generate-api-test-should-perform-blank-page-detection"
                      checked={shouldPerformBlankPageDetection}
                      onChange={(e) =>
                        setShouldPerformBlankPageDetection(e.target.checked)
                      }
                    />
                  }
                  label="Should Perform Blank Page Detection"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Folder Paths */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Folder Paths
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Production Temporary Folder Path"
                  value={productionTempararyFolderPath}
                  onChange={(e) =>
                    setProductionTempararyFolderPath(e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Production Destination Folder Path"
                  value={productionDestinationFolderPath}
                  onChange={(e) =>
                    setProductionDestinationFolderPath(e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Production Folder Path"
                  value={productionFolderPath}
                  onChange={(e) => setProductionFolderPath(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExecute}
              disabled={executeMutation.isPending}
            >
              {executeMutation.isPending
                ? '⏳ Executing...'
                : '🚀 Execute Generate'}
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              🔄 Reset
            </Button>
            {executeMutation.data && (
              <Button variant="outlined" onClick={handleCopyJson}>
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Loading State */}
      {executeMutation.isPending && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ Executing generation... Please wait...
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {executeMutation.isError && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {executeMutation.error?.message ||
                  'An error occurred while executing generation'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Success State */}
      {executeMutation.isSuccess && executeMutation.data && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Execute Result
              </Typography>

              <Alert severity="success" sx={{marginBottom: 2}}>
                <Typography className="typo-text subhead3_size" gutterBottom>
                  ✅ Success
                </Typography>
                <Typography className="typo-text body3_size">
                  {executeMutation.data.message}
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(executeMutation.data, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Result Display */}
      {executeResult && !executeMutation.isPending && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Execute Result
            </Typography>
            <Alert
              severity={executeResult.includes('✅') ? 'success' : 'error'}
            >
              {executeResult}
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ExecuteGenerateApiTest;
